import { ENUM_LABELS, type OrderRequestRow, type OrderRequestStatus } from '#shared/types';
import { Badge, type BadgeTone } from '#client/components/ui/Badge';
import type { Column } from '#client/components/ui/DataTable';
import { formatDateTime } from '#client/lib/format';

const TONES: Record<OrderRequestStatus, BadgeTone> = {
  // Amber, because a waiting request is somebody's next action rather than a
  // neutral fact.
  pending: 'warning',
  registered: 'success',
  declined: 'neutral',
};

/**
 * The request inbox table.
 *
 * At module scope, like every other column array in the app, so it is not
 * reallocated on every render.
 */
export const orderRequestColumns: Column<OrderRequestRow>[] = [
  {
    key: 'patient',
    header: 'Patient',
    frozen: true,
    render: (row) => (
      <div>
        <strong>{row.patient_name}</strong>
        <div className="c-muted">
          {row.number}
          {row.phone_number && ` · ${row.phone_number}`}
        </div>
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '9rem',
    render: (row) => (
      <Badge tone={TONES[row.status]}>{ENUM_LABELS.orderRequestStatus[row.status]}</Badge>
    ),
  },
  {
    key: 'tests',
    header: 'Asked for',
    render: (row) => (
      <span className="c-order-tests">
        {row.lines.map((line) => (
          <span
            key={line.id}
            // An unmapped line is the receptionist's next decision, so it is
            // marked rather than shown as ordinary text.
            className={
              line.test_id === null ? 'c-order-test c-order-test--unmapped' : 'c-order-test'
            }
            title={line.test_id === null ? 'Not matched to a test yet' : line.lab_test?.name}
          >
            {line.lab_test?.name ?? line.requested_text}
          </span>
        ))}
      </span>
    ),
  },
  {
    key: 'from',
    header: 'From',
    width: '12rem',
    render: (row) => (
      <div>
        {row.referred_by ?? <span className="c-muted">Not given</span>}
        {row.source && <div className="c-muted">{row.source}</div>}
      </div>
    ),
  },
  {
    key: 'received',
    header: 'Received',
    width: '12rem',
    render: (row) => formatDateTime(row.created_at),
  },
];
